import gql from 'graphql-tag';
import { Mutation, Query, QueryRegistry } from 'core';
import { SHARED_FILE_REVISION } from 'apps/share/gql';
import { ADDRESS_FIELDS } from 'apps/addresses/gql';
import { FILE_FIELDS } from 'apps/file/gql';

const MATERIAL_FIELDS = gql`
  fragment MaterialFields on Material {
    id
    name
    shortDescription
    tags {
      id
      label
    }
    count(filter: $materialFilter)
    isSelectable
    parentMaterial {
      id
    }
  }
`;

const MATERIALS = gql`
  ${MATERIAL_FIELDS}
  query GetMaterials($materialFilter: MaterialFilter) {
    materials(filter: $materialFilter) {
      ...MaterialFields
      childMaterials(filter: $materialFilter) {
        ...MaterialFields
        childMaterials(filter: $materialFilter) {
          ...MaterialFields
          childMaterials(filter: $materialFilter) {
            ...MaterialFields
            childMaterials(filter: $materialFilter) {
              ...MaterialFields
            }
          }
        }
      }
    }
  }
`;

export class MaterialsQuery extends Query {
  static conf = {
    GQL: MATERIALS,
    varNames: ['materialFilter'],
    resultPath: 'materials',
  };
}
QueryRegistry.add('rfq.MaterialsQuery', MaterialsQuery);

const PROCESS_FIELDS = gql`
  fragment ProcessFields on Process {
    id
    name
    shortDescription
    tags {
      id
      label
    }
    count(filter: $processFilter)
    isSelectable
    parentProcess {
      id
    }
  }
`;

const PROCESSES = gql`
  ${PROCESS_FIELDS}
  query GetProcesses($processFilter: ProcessFilter) {
    processes(filter: $processFilter) {
      ...ProcessFields
      childProcesses(filter: $processFilter) {
        ...ProcessFields
        childProcesses(filter: $processFilter) {
          ...ProcessFields
          childProcesses(filter: $processFilter) {
            ...ProcessFields
          }
        }
      }
    }
  }
`;

export class ProcessesQuery extends Query {
  static conf = {
    GQL: PROCESSES,
    varNames: ['processFilter'],
    resultPath: 'processes',
  };
}

QueryRegistry.add('rfq.ProcessesQuery', ProcessesQuery);

// Add unit and default to Option field
const OPTIONS = gql`
  query options($optionFilter: OptionFilter) {
    options(filter: $optionFilter) {
      id
      name
      valueType
      unit
      nilLabel
      required
      prefix
      ... on MinMaxOption {
        type
      }
      for {
        ... on Material {
          __typename
          id
          name
        }
        ... on Process {
          __typename
          id
          name
        }
      }
      values(filter: $optionFilter) {
        id
        option {
          id
        }
        default
        valueInt
        valueFloat
        valueString
        valueBool
      }
    }
  }
`;
/*
Option Instances gql:

instances(filter: $optionFilter) {
  id
  option {
    id
  }
  ForMaterial {
    id
    name
  }
  ForProcess {
    id
    name
  }
  valueInt
  valueFloat
  valueString
  valueBool
} */

export class OptionsQuery extends Query {
  static conf = {
    GQL: OPTIONS,
    varNames: ['optionFilter'],
    resultPath: 'options',
  };
}

const CREATE_RFQ = gql`
  ${ADDRESS_FIELDS}
  mutation createRFQ($input: CreateRequestForQuotesInput!, $spaceId: ID) {
    createRequestForQuote(input: $input, spaceId: $spaceId) {
      id
      type
      jobSpec {
        id
        build {
          id
          process {
            id
            name
          }
          material {
            id
            name
          }
          values {
            of {
              id
              valueType
            }
            valueFloat
            valueInt
            valueBool
            valueString
          }
        }
        quantity
      }
      address {
        ...AddressFields
      }
    }
  }
`;

export class CreateRFQMutation extends Mutation {
  static conf = {
    GQL: CREATE_RFQ,
    inputName: ['input', 'spaceId'],
    resultPath: 'createRequestForQuote',
  };
}

const FILE_REQUIREMENTS_FRAGMENT = gql`
  fragment fileRequirementsFields on FileRequirement {
    id
    for {
      id
      name
    }
    formats
    type
    operator
    valueInt
    valueFloat
    valueString
    valueBool
  }
`;

const BUILD_FRAGMENT = gql`
  fragment buildFields on Build {
    id
    process {
      id
      name
      parentProcess {
        id
        name
      }
    }
    material {
      id
      name
      parentMaterial {
        id
        name
        parentMaterial {
          id
          name
          parentMaterial {
            id
            name
          }
        }
      }
    }
    values {
      of {
        id
        valueType
        name
        unit
        nilLabel
        prefix
      }
      valueFloat
      valueInt
      valueBool
      valueString
    }
  }
`;

const RFQ_QUERY = gql`
  ${FILE_FIELDS}
  ${SHARED_FILE_REVISION}
  ${ADDRESS_FIELDS}
  ${FILE_REQUIREMENTS_FRAGMENT}
  query RequestForQuote($id: ID!, $conversionSpec: String) {
    requestForQuote(id: $id) {
      id
      state
      createdAt
      nextPossibleStates
      isQuotable
      isUpdatable
      customerSpace {
        id
        spaceType
        content {
          id
          slug
          title
        }
      }
      intermediatorSession {
        id
        shareFile {
          id
          name
          context {
            id
          }
          revision {
            ...sharedFileRevision
          }
          firstFile {
            ...fileFields
          }
        }
      }
      quotingPriorityData
      fileRequirements {
        ...fileRequirementsFields
      }
      address {
        ...AddressFields
      }
      jobSpec {
        id
        quantity
        leadTime
        build {
          id
          file {
            id
          }
          process {
            id
            name
            parentProcess {
              id
              name
            }
          }
          material {
            id
            name
            parentMaterial {
              id
              name
              parentMaterial {
                id
                name
                parentMaterial {
                  id
                  name
                }
              }
            }
          }
          values {
            of {
              id
              valueType
              name
              unit
              nilLabel
              prefix
            }
            valueFloat
            valueInt
            valueBool
            valueString
          }
          supportingFiles {
            id
            filename
            permalink
            fileLastModified
          }
          body {
            id
            cadId
            srcFile {
              id
              path
              url
              mimeType
              filename
              size
              completed
              cancelled
              permalink
              originalFile {
                id
              }
              conversionGltfjson(conversionSpec: $conversionSpec) {
                id
                status
                reason
                errorCode
                convertedFile {
                  id
                }
              }
              creator {
                id
                username
              }
            }
            status
            error
          }
        }
      }
      shipmentQuotes {
        id
        totalCost
        productQuotes {
          id
          finalCost
          validUntil
          currency
          for {
            id
            quantity
            build {
              id
              process {
                id
                name
                parentProcess {
                  id
                  name
                }
              }
              material {
                id
                name
                parentMaterial {
                  id
                  name
                }
              }
            }
          }
          from {
            id
          }
          supplier {
            id
            address {
              id
              country
            }
            companyName
          }
        }
        shippingQuote {
          id
          deliveryTime
        }
      }
      sessions {
        supplier {
          initiative {
            id
            title
            avatar {
              url
            }
          }
        }
      }
    }
  }
`;

export class RFQQuery extends Query {
  static conf = {
    GQL: RFQ_QUERY,
    resultPath: 'requestForQuote',
  };
}

const RFQLIST_FIELD_FRAGMENT = gql`
  ${BUILD_FRAGMENT}
  fragment rfqFields on RequestForQuote {
    id
    intermediatorSession {
      id
      supplier {
        id
      }
      state
      shareFile {
        id
        name
        revision {
          id
          file {
            id
            filename
            completed
            conversionGltfjson {
              id
              status
              reason
              errorCode
            }
          }
        }
      }
    }
    state
    createdAt
    jobSpec {
      id
      quantity
      build {
        ...buildFields
      }
    }
  }
`;
// quotes {
//   id
// }

const RFQ_CONNECTION_QUERY = gql`
  ${RFQLIST_FIELD_FRAGMENT}

  query RFQs(
    $first: Int
    $offset: Int
    $sortField: String
    $sortOrder: SortOrder
    $filters: [FilterInput!]
    $spaceId: ID
  ) {
    requestForQuotes(
      first: $first
      offset: $offset
      sortField: $sortField
      sortOrder: $sortOrder
      filters: $filters
      spaceId: $spaceId
    ) {
      total
      nodes {
        ...rfqFields
        customerSpace {
          id
          content {
            id
            slug
            title
          }
          spaceType
        }
      }
    }
  }
`;

const RFQ_INTERMEDIATOR_CONNECTION_QUERY = gql`
  ${RFQLIST_FIELD_FRAGMENT}

  query IntermediatorRFQs(
    $first: Int
    $offset: Int
    $sortField: String
    $sortOrder: SortOrder
    $filters: [FilterInput!]
    $spaceId: ID
  ) {
    requestForQuotes(
      first: $first
      offset: $offset
      sortField: $sortField
      sortOrder: $sortOrder
      filters: $filters
      spaceId: $spaceId
    ) {
      total
      nodes {
        ...rfqFields
        isQuotable
        customerSpace {
          id
          content {
            id
            slug
            title
            avatar {
              id
              filename
              path
              url
            }
            inSpace {
              id
            }
            imageFallbackChar
          }
          spaceType
        }
      }
    }
  }
`;

// Possible additional selections:
// Rfq:
//   - estimatedAwardDate
//   - quotesNeededBy
// Build:
//   If we want to display options
//   - values {
//       valueInt
//       valueBool
//       valueFloat
//       valueString
//       of {
//         id
//         ...
//       }
//     }

export class RFQConnectionQuery extends Query {
  static conf = {
    GQL: RFQ_CONNECTION_QUERY,
    resultPath: 'requestForQuotes',
    varNames: [
      'first',
      'offset',
      'sortField',
      'sortOrder',
      'filters',
      'spaceId',
    ],
  };
}

export class RFQIntermediatorConnectionQuery extends Query {
  static conf = {
    GQL: RFQ_INTERMEDIATOR_CONNECTION_QUERY,
    resultPath: 'requestForQuotes',
    varNames: [
      'first',
      'offset',
      'sortField',
      'sortOrder',
      'filters',
      'spaceId',
    ],
  };
}

/* We could just get address IDs here and get the Address once to show it */
export const JOB_SPEC_FRAGMENT = gql`
  ${BUILD_FRAGMENT}
  fragment jobSpecFields on JobSpec {
    id
    quantity
    leadTime
    build {
      ...buildFields
    }
  }
`;

const RFQ_PRODUCT_QUOTES = gql`
  ${JOB_SPEC_FRAGMENT}
  query RfqProductQuotes($id: ID!) {
    requestForQuoteList(ids: [$id]) {
      productQuotes {
        id
        cost
        currency
        validUntil
        isInstantQuote
        from {
          id
        }
        supplier {
          id
          companyName
        }
        for {
          ...jobSpecFields
        }
      }
    }
  }
`;

export class RFQProductQuotesQ extends Query {
  static conf = {
    GQL: RFQ_PRODUCT_QUOTES,
    resultPath: 'requestForQuoteList.0.productQuotes',
    varNames: ['id'],
  };
}

const RFQ_LIST_BY_IDS_QUERY = gql`
  ${JOB_SPEC_FRAGMENT}
  query rfqsByIds($ids: [ID!]!) {
    requestForQuoteList(ids: $ids) {
      id
      address {
        id
        name
        company
        address1
        address2
        zip
        city
        province
        country
        phone
        default
      }
      customerSpace {
        id
      }
      isQuotable
      jobSpec {
        ...jobSpecFields
        build {
          file {
            id
            filename
            permalink
          }
          supportingFiles {
            id
            filename
            permalink
            fileLastModified
          }
        }
      }
    }
  }
`;

export class RFQListByIDSQuery extends Query {
  static conf = {
    GQL: RFQ_LIST_BY_IDS_QUERY,
    resultPath: 'requestForQuoteList',
    varNames: ['ids'],
  };
}

const ADD_QUOTING_PRIORITY_MUTATION = gql`
  mutation AddQuotingPriority($input: AddQuotingPriorityInput!) {
    addQuotingPriority(input: $input) {
      quotingPriorityData
    }
  }
`;

export class AddQuotingPriorityMutation extends Mutation {
  static conf = {
    GQL: ADD_QUOTING_PRIORITY_MUTATION,
    inputName: ['input'],
  };
}

const ASSIGN_RFQ_TO_MANUFACTURER = gql`
  mutation AssignRequestForQuote($input: AssignRequestForQuoteInput!) {
    assignRequestForQuote(input: $input) {
      id
      state
      type
      createdAt
    }
  }
`;

export class AssignRFQToManufacturerMutation extends Mutation {
  static conf = {
    GQL: ASSIGN_RFQ_TO_MANUFACTURER,
    inputName: ['input'],
  };
}

const UPDATE_RFQ = gql`
  ${FILE_REQUIREMENTS_FRAGMENT}
  ${ADDRESS_FIELDS}
  mutation UpdateRequestForQuote($input: UpdateRequestForQuoteInput!) {
    updateRequestForQuote(input: $input) {
      id
      isUpdatable
      isQuotable
      address {
        ...AddressFields
      }
      jobSpec {
        id
        build {
          id
          supportingFiles {
            id
            filename
            permalink
            fileLastModified
          }
        }
      }
      fileRequirements {
        ...fileRequirementsFields
      }
    }
  }
`;

export class UpdateRFQMutation extends Mutation {
  static conf = {
    GQL: UPDATE_RFQ,
    inputName: ['input'],
    resultPath: 'updateRequestForQuote',
  };
}

const FILE_REQUIREMENTS_QUERY = gql`
  ${FILE_REQUIREMENTS_FRAGMENT}
  query FileRequirements($build: CreateBuildInput!) {
    fileRequirements(build: $build) {
      ...fileRequirementsFields
    }
  }
`;

export class FileRequirementsQuery extends Query {
  static conf = {
    GQL: FILE_REQUIREMENTS_QUERY,
    varNames: ['build'],
    resultPath: 'fileRequirements',
  };
}

const UPDATE_RFQ_STATE = gql`
  mutation UpdateRequestForQuoteState(
    $input: UpdateRequestForQuoteStateInput!
  ) {
    updateRequestForQuoteState(input: $input) {
      id
      state
      nextPossibleStates
      isQuotable
      isUpdatable
    }
  }
`;

export class UpdateRequestForQuoteStateMutation extends Mutation {
  static conf = {
    GQL: UPDATE_RFQ_STATE,
    inputName: ['input'],
    resultPath: 'updateRequestForQuoteState',
  };
}
